import { Folder, FileText, FileImage, HardDrive, Lock } from 'lucide-react';

/**
 * Statischer Geraete-Mockup fuer den Hero der Landingpage.
 * Rein dekorativ, keine echten Daten. Die Farben sind bewusst fest verdrahtet
 * (Kopie der .vault-app Dark-Palette), damit der Screen unabhaengig vom
 * Seiten-Theme und vom vault-app-Scope immer gleich aussieht.
 */

const SCREEN = {
  bg: '#141e30',
  fg: '#f1f5f9',
  muted: '#94a3b8',
  subtle: '#64748b',
  accent: '#10b981',
  accentSoft: 'rgba(16, 185, 129, 0.14)',
  surface: 'rgba(255, 255, 255, 0.06)',
  border: 'rgba(255, 255, 255, 0.10)',
};

const FOLDERS = [
  { name: 'Dokumente', meta: '18 Dateien' },
  { name: 'Bewerbung', meta: '7 Dateien' },
  { name: 'Versicherungen', meta: '11 Dateien' },
  { name: 'Steuer 2025', meta: '24 Dateien' },
];

const FILES = [
  { name: 'Arbeitsvertrag.pdf', size: '1,2 MB', kind: 'pdf' },
  { name: 'Lebenslauf.pdf', size: '340 KB', kind: 'pdf' },
  { name: 'Passkopie.jpg', size: '2,8 MB', kind: 'img' },
  { name: 'Zeugnisse.pdf', size: '890 KB', kind: 'pdf' },
];

function SectionLabel({ children }) {
  return (
    <div
      className="uppercase mb-2"
      style={{
        fontFamily: 'var(--ed-mono)',
        fontSize: '9px',
        letterSpacing: '0.16em',
        color: SCREEN.subtle,
      }}
    >
      {children}
    </div>
  );
}

export default function HeroVaultPreview() {
  return (
    <figure className="m-0 w-full max-w-[300px] mx-auto">
      {/* Geraeterahmen */}
      <div
        className="relative rounded-[2.6rem] p-[10px]"
        role="img"
        aria-label="Beispielansicht der Tresor-App auf einem Smartphone mit Ordnern und Dateien"
        style={{
          background: 'linear-gradient(155deg, #38424f 0%, #161d26 45%, #0a0f15 100%)',
          boxShadow:
            '0 34px 70px -24px rgba(20, 32, 27, 0.55), 0 2px 6px rgba(20, 32, 27, 0.25), inset 0 0 0 1px rgba(255, 255, 255, 0.08)',
        }}
      >
        {/* Seitentasten */}
        <span
          className="absolute rounded-l-sm"
          style={{ left: '-2px', top: '110px', width: '2px', height: '28px', background: '#232b34' }}
        />
        <span
          className="absolute rounded-l-sm"
          style={{ left: '-2px', top: '150px', width: '2px', height: '46px', background: '#232b34' }}
        />
        <span
          className="absolute rounded-r-sm"
          style={{ right: '-2px', top: '135px', width: '2px', height: '64px', background: '#232b34' }}
        />

        {/* Screen */}
        <div
          className="relative overflow-hidden rounded-[2.05rem]"
          style={{ background: SCREEN.bg, color: SCREEN.fg, aspectRatio: '280 / 590' }}
        >
          {/* Statusleiste */}
          <div className="flex items-center justify-between px-5 pt-3">
            <span
              style={{ fontFamily: 'var(--ed-mono)', fontSize: '10px', color: SCREEN.fg }}
              className="tabular-nums"
            >
              09:41
            </span>
            <span className="flex items-end gap-[3px]" style={{ height: '9px' }}>
              <span style={{ width: '2px', height: '4px', borderRadius: '1px', background: SCREEN.fg }} />
              <span style={{ width: '2px', height: '6px', borderRadius: '1px', background: SCREEN.fg }} />
              <span style={{ width: '2px', height: '8px', borderRadius: '1px', background: SCREEN.fg }} />
              <span
                className="ml-1"
                style={{
                  width: '16px',
                  height: '8px',
                  borderRadius: '2px',
                  border: `1px solid ${SCREEN.muted}`,
                  padding: '1px',
                  display: 'inline-block',
                }}
              >
                <span
                  className="block"
                  style={{ width: '70%', height: '100%', borderRadius: '1px', background: SCREEN.accent }}
                />
              </span>
            </span>
          </div>

          {/* Dynamic Island */}
          <span
            className="absolute left-1/2 -translate-x-1/2 rounded-full"
            style={{ top: '10px', width: '72px', height: '18px', background: '#05080c' }}
          />

          {/* App-Kopf */}
          <div className="flex items-center justify-between px-4 pt-5">
            <span className="flex items-center gap-2">
              <HardDrive style={{ width: '15px', height: '15px', color: SCREEN.accent }} />
              <span className="font-extrabold tracking-tight" style={{ fontSize: '14px' }}>
                Tresor
              </span>
            </span>
            <span
              className="flex items-center gap-1 rounded-full"
              style={{
                background: SCREEN.accentSoft,
                color: SCREEN.accent,
                padding: '3px 7px',
                fontSize: '8px',
                letterSpacing: '0.08em',
                fontFamily: 'var(--ed-mono)',
              }}
            >
              <Lock style={{ width: '9px', height: '9px' }} />
              ENTSPERRT
            </span>
          </div>

          <div
            className="px-4 pt-3 pb-2 uppercase"
            style={{
              fontFamily: 'var(--ed-mono)',
              fontSize: '9px',
              letterSpacing: '0.14em',
              color: SCREEN.muted,
            }}
          >
            Mein Tresor
          </div>

          {/* Inhalt */}
          <div className="px-4 pt-1">
            <SectionLabel>Ordner</SectionLabel>
            <div className="flex flex-col gap-[6px] mb-4">
              {FOLDERS.map((folder) => (
                <div
                  key={folder.name}
                  className="flex items-center gap-[10px] rounded-xl"
                  style={{
                    background: SCREEN.surface,
                    border: `1px solid ${SCREEN.border}`,
                    padding: '8px 10px',
                  }}
                >
                  <Folder
                    style={{ width: '17px', height: '17px', color: SCREEN.accent, flexShrink: 0 }}
                    fill="currentColor"
                    fillOpacity={0.2}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block font-medium truncate" style={{ fontSize: '11.5px' }}>
                      {folder.name}
                    </span>
                    <span className="block truncate" style={{ fontSize: '9px', color: SCREEN.muted }}>
                      {folder.meta}
                    </span>
                  </span>
                </div>
              ))}
            </div>

            <SectionLabel>Dateien</SectionLabel>
            <div
              className="rounded-xl overflow-hidden"
              style={{ background: SCREEN.surface, border: `1px solid ${SCREEN.border}` }}
            >
              {FILES.map((file, i) => (
                <div
                  key={file.name}
                  className="flex items-center gap-[10px]"
                  style={{
                    padding: '8px 10px',
                    borderTop: i === 0 ? 'none' : `1px solid ${SCREEN.border}`,
                  }}
                >
                  {file.kind === 'pdf' ? (
                    <FileText style={{ width: '16px', height: '16px', color: '#f87171', flexShrink: 0 }} />
                  ) : (
                    <FileImage style={{ width: '16px', height: '16px', color: '#60a5fa', flexShrink: 0 }} />
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block font-medium truncate" style={{ fontSize: '11.5px' }}>
                      {file.name}
                    </span>
                  </span>
                  <span
                    className="tabular-nums"
                    style={{ fontSize: '9px', color: SCREEN.muted, fontFamily: 'var(--ed-mono)' }}
                  >
                    {file.size}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Speicherleiste unten */}
          <div
            className="absolute inset-x-0 bottom-0 px-4 pt-6 pb-4"
            style={{
              background: `linear-gradient(to top, ${SCREEN.bg} 62%, rgba(15, 23, 42, 0))`,
            }}
          >
            <div className="flex items-center justify-between mb-[6px]">
              <span
                style={{ fontFamily: 'var(--ed-mono)', fontSize: '9px', color: SCREEN.muted }}
              >
                48,2 GB von 200 GB
              </span>
              <span
                style={{ fontFamily: 'var(--ed-mono)', fontSize: '9px', color: SCREEN.subtle, letterSpacing: '0.1em' }}
              >
                STARTER
              </span>
            </div>
            <div
              className="w-full rounded-full overflow-hidden"
              style={{ height: '3px', background: 'rgba(255, 255, 255, 0.10)' }}
            >
              <div style={{ width: '24%', height: '100%', background: SCREEN.accent }} />
            </div>
            <div
              className="mx-auto mt-4 rounded-full"
              style={{ width: '96px', height: '4px', background: 'rgba(255, 255, 255, 0.22)' }}
            />
          </div>
        </div>
      </div>

      <figcaption
        className="ed-muted mt-4 mx-auto text-center max-w-[16.5rem]"
        style={{ fontFamily: 'var(--ed-mono)', fontSize: '11px', lineHeight: 1.55 }}
      >
        Beispielansicht. Nur Ihr Passwort entschlüsselt die Dateien.
      </figcaption>
    </figure>
  );
}
